import { MdAlign, MdBlock, MdInline } from './markdown.models';

// ---------------------------------------------------------------------------
// Inline parser — ported from the debug UI appendInlineMarkdown
// ---------------------------------------------------------------------------

const INLINE_PATTERN = /(`[^`]+`|\*\*[^*]+\*\*|\*[^*]+\*|\[[^\]]+]\([^)]+\))/g;

const isSafeHref = (href: string): boolean => /^(https?:|mailto:|\/)/i.test(href);

const parseInlineNode = (raw: string): MdInline => {
  if (raw.startsWith('`')) {
    return { kind: 'code', value: raw.slice(1, -1) };
  }
  if (raw.startsWith('**')) {
    return { kind: 'strong', inlines: parseInline(raw.slice(2, -2)) };
  }
  if (raw.startsWith('*')) {
    return { kind: 'em', inlines: parseInline(raw.slice(1, -1)) };
  }
  const link = raw.match(/^\[([^\]]+)]\(([^)]+)\)$/);
  if (link) {
    const href = isSafeHref(link[2]) ? link[2] : undefined;
    return { kind: 'link', label: link[1], href };
  }
  return { kind: 'text', value: raw };
};

export const parseInline = (text: string): MdInline[] => {
  const result: MdInline[] = [];
  let lastIndex = 0;
  for (const match of text.matchAll(INLINE_PATTERN)) {
    if (match.index !== undefined && match.index > lastIndex) {
      result.push({ kind: 'text', value: text.slice(lastIndex, match.index) });
    }
    result.push(parseInlineNode(match[0]));
    lastIndex = (match.index ?? 0) + match[0].length;
  }
  if (lastIndex < text.length) {
    result.push({ kind: 'text', value: text.slice(lastIndex) });
  }
  return result;
};

// ---------------------------------------------------------------------------
// Table helpers
// ---------------------------------------------------------------------------

const parseTableRow = (line: string): string[] => {
  const trimmed = line.trim().replace(/^\|/, '').replace(/\|$/, '');
  return trimmed.split('|').map((cell) => cell.trim());
};

const parseTableAlignments = (separatorLine: string): MdAlign[] =>
  parseTableRow(separatorLine).map((cell) => {
    const startsWithColon = cell.startsWith(':');
    const endsWithColon = cell.endsWith(':');
    if (startsWithColon && endsWithColon) return 'center';
    if (endsWithColon) return 'right';
    return 'left';
  });

const isTableRow = (line: string): boolean => line.includes('|') && !line.trimStart().startsWith('```');

const isTableSeparator = (line: string): boolean => /^\s*\|?\s*:?-{3,}:?\s*(\|\s*:?-{3,}:?\s*)+\|?\s*$/.test(line);

const isTableStart = (lines: string[], index: number): boolean =>
  index + 1 < lines.length && isTableRow(lines[index]) && isTableSeparator(lines[index + 1]);

const isMarkdownBlockStart = (line: string): boolean =>
  line.trimStart().startsWith('```') ||
  /^#{1,3}\s+/.test(line) ||
  /^>\s?/.test(line) ||
  /^\s*[-*]\s+/.test(line) ||
  /^\s*\d+\.\s+/.test(line);

// ---------------------------------------------------------------------------
// Block parsers
// ---------------------------------------------------------------------------

const appendCodeBlock = (lines: string[], startIndex: number): [MdBlock, number] => {
  const codeLines: string[] = [];
  let index = startIndex + 1;
  while (index < lines.length && !lines[index].trimStart().startsWith('```')) {
    codeLines.push(lines[index]);
    index++;
  }
  const block: MdBlock = { kind: 'code-block', code: codeLines.join('\n') };
  return [block, index < lines.length ? index + 1 : index];
};

const appendHeading = (line: string): MdBlock => {
  const match = line.match(/^(#{1,3})\s+(.*)$/)!;
  const level = match[1].length as 1 | 2 | 3;
  return { kind: 'heading', level, inlines: parseInline(match[2]) };
};

const appendBlockquote = (lines: string[], startIndex: number): [MdBlock, number] => {
  const quoteLines: string[] = [];
  let index = startIndex;
  while (index < lines.length && /^>\s?/.test(lines[index])) {
    quoteLines.push(lines[index].replace(/^>\s?/, ''));
    index++;
  }
  return [{ kind: 'blockquote', inlines: parseInline(quoteLines.join('\n')) }, index];
};

const appendTable = (lines: string[], startIndex: number): [MdBlock, number] => {
  const headerCells = parseTableRow(lines[startIndex]);
  const aligns = parseTableAlignments(lines[startIndex + 1]);
  const header = headerCells.map((cell) => parseInline(cell));
  const rows: MdInline[][][] = [];
  let index = startIndex + 2;
  while (index < lines.length && isTableRow(lines[index])) {
    const cells = parseTableRow(lines[index]);
    rows.push(headerCells.map((_, i) => parseInline(cells[i] ?? '')));
    index++;
  }
  return [{ kind: 'table', aligns, header, rows }, index];
};

const appendList = (lines: string[], startIndex: number, ordered: boolean): [MdBlock, number] => {
  const itemPattern = ordered ? /^\s*\d+\.\s+/ : /^\s*[-*]\s+/;
  const items: MdInline[][] = [];
  let index = startIndex;
  while (index < lines.length && itemPattern.test(lines[index])) {
    items.push(parseInline(lines[index].replace(itemPattern, '')));
    index++;
  }
  return [{ kind: 'list', ordered, items }, index];
};

const appendParagraph = (lines: string[], startIndex: number): [MdBlock, number] => {
  const paragraphLines: string[] = [];
  let index = startIndex;
  while (index < lines.length && lines[index].trim() && !isMarkdownBlockStart(lines[index]) && !isTableStart(lines, index)) {
    paragraphLines.push(lines[index]);
    index++;
  }
  return [{ kind: 'paragraph', inlines: parseInline(paragraphLines.join('\n')) }, index];
};

// ---------------------------------------------------------------------------
// Public entry point
// ---------------------------------------------------------------------------

export const parseMarkdown = (source: string): MdBlock[] => {
  const blocks: MdBlock[] = [];
  const lines = (source ?? '').split('\n');
  let index = 0;
  while (index < lines.length) {
    const line = lines[index];
    if (!line.trim()) {
      index++;
      continue;
    }
    if (line.trimStart().startsWith('```')) {
      const [block, nextIndex] = appendCodeBlock(lines, index);
      blocks.push(block);
      index = nextIndex;
    } else if (/^#{1,3}\s+/.test(line)) {
      blocks.push(appendHeading(line));
      index++;
    } else if (isTableStart(lines, index)) {
      const [block, nextIndex] = appendTable(lines, index);
      blocks.push(block);
      index = nextIndex;
    } else if (/^>\s?/.test(line)) {
      const [block, nextIndex] = appendBlockquote(lines, index);
      blocks.push(block);
      index = nextIndex;
    } else if (/^\s*[-*]\s+/.test(line)) {
      const [block, nextIndex] = appendList(lines, index, false);
      blocks.push(block);
      index = nextIndex;
    } else if (/^\s*\d+\.\s+/.test(line)) {
      const [block, nextIndex] = appendList(lines, index, true);
      blocks.push(block);
      index = nextIndex;
    } else {
      const [block, nextIndex] = appendParagraph(lines, index);
      blocks.push(block);
      index = nextIndex;
    }
  }
  return blocks;
};
